import { ProgrammingServiceOrder } from 'src/app/entities/order';

/**
 * Canonical constant values for food types (tipoAlimento).
 */
export const TIPO_ALIMENTO = {
  ALIM_TREK: 'ALIM TREK',
  PICNIC: 'PICNIC',
  BOX_LUNCH: 'BOX LUNCH',
  SNACK: 'SNACK',
  DONATIVO: 'DONATIVO',
} as const;

export type TipoAlimento = (typeof TIPO_ALIMENTO)[keyof typeof TIPO_ALIMENTO];

export const TIPO_ALIMENTOS_LIST: string[] = [
  TIPO_ALIMENTO.ALIM_TREK,
  TIPO_ALIMENTO.PICNIC,
  TIPO_ALIMENTO.BOX_LUNCH,
  TIPO_ALIMENTO.SNACK,
  TIPO_ALIMENTO.DONATIVO,
];

/**
 * Canonical constant values for food service beneficiaries (destinatarioServicio).
 */
export const DESTINATARIO_SERVICIO = {
  PERSONAL_DE_CAMPO: 'PERSONAL DE CAMPO',
  PAX: 'PAX',
  TC: 'TC',
  STAFF: 'STAFF',
  ASIST_GUIA: 'ASIST GUIA',
  GUIA: 'GUIA',
  CHOFER: 'CHOFER',
} as const;

export type DestinatarioServicio =
  (typeof DESTINATARIO_SERVICIO)[keyof typeof DESTINATARIO_SERVICIO];

export const DESTINATARIO_SERVICIO_LIST: string[] = [
  DESTINATARIO_SERVICIO.PERSONAL_DE_CAMPO,
  DESTINATARIO_SERVICIO.PAX,
  DESTINATARIO_SERVICIO.TC,
  DESTINATARIO_SERVICIO.STAFF,
  DESTINATARIO_SERVICIO.ASIST_GUIA,
  DESTINATARIO_SERVICIO.GUIA,
  DESTINATARIO_SERVICIO.CHOFER,
];

/**
 * Represents a service item associated with a file or itinerary row.
 */
export interface PicnicServiceDetailItem {
  id: string;
  nombreServicio: string;
  tipoServicio: string;
  categoriaServicio: string;
  nombreProveedor: string;
  idProveedor?: string;
  locacion?: string;
  ssffproduct?: boolean | null;
  serviceAdd?: boolean | null;
  productId?: string | null;
  isSSFFPadre?: boolean | null;
  serviceSnapshot?: {
    locacion?: string;
    nombreServicio?: string;
    tipoServicio?: string;
    categoriaServicio?: string;
    [key: string]: any;
  };
  foodType?: string;
  destinatarioServicio?: string;
  orderId?: string | null;
  orderNumber?: string | number | null;
  ordenServicioId?: string | null;
  ordenServicioStatus?: string | null;
  numOS?: string | number | null;
  order?: ProgrammingServiceOrder | null;
}

/**
 * Standard multiselect options for dietary restrictions.
 */
export const FOOD_RESTRICTION_OPTIONS = [
  { value: 'ninguna', label: 'Ninguna' },
  { value: 'vegetariano', label: 'Vegetariano' },
  { value: 'ovo-lactovegetariano', label: 'Ovo-Lactovegetariano' },
  { value: 'lacto-vegetariano', label: 'Lacto-Vegetariano' },
  { value: 'vegano', label: 'Vegano' },
  { value: 'crudi-vegano', label: 'Crudi-Vegano' },
  { value: 'pesquetariano', label: 'Pesquetariano' },
  { value: 'celiaco/gluten free', label: 'Celiaco/Gluten free' },
  { value: 'no lacteos', label: 'No lacteos' },
  { value: 'paleodieta', label: 'Paleodieta' },
  { value: 'alergias alimentarias', label: 'Alergias alimentarias' },
  { value: 'otros', label: 'Otros' },
] as const;

/**
 * Normalization lookup mapping raw restriction terms to clean formatted display labels.
 */
export const FOOD_RESTRICTION_LABEL_LOOKUP: Record<string, string> = {
  ninguna: 'Ninguna',
  vegetariano: 'Vegetariano',
  'ovo-lactovegetariano': 'Ovo-Lactovegetariano',
  'ovo lactovegetariano': 'Ovo-Lactovegetariano',
  'lacto-vegetariano': 'Lacto-Vegetariano',
  'lacto vegetariano': 'Lacto-Vegetariano',
  vegano: 'Vegano',
  'crudi-vegano': 'Crudi-Vegano',
  'crudi vegano': 'Crudi-Vegano',
  pesquetariano: 'Pesquetariano',
  pescetariano: 'Pescetariano',
  'celiaco/gluten free': 'Celiaco/Gluten free',
  celiaco: 'Celiaco/Gluten free',
  'gluten free': 'Celiaco/Gluten free',
  'sin gluten': 'Celiaco/Gluten free',
  'no lacteos': 'No lácteos',
  'no lácteos': 'No lácteos',
  'sin lactosa': 'No lácteos',
  'sin lacteos': 'No lácteos',
  paleodieta: 'Paleodieta',
  paleo: 'Paleodieta',
  'alergias alimentarias': 'Alergias alimentarias',
  'alergia alimentaria': 'Alergias alimentarias',
  alergias: 'Alergias alimentarias',
  alergia: 'Alergias alimentarias',
  otros: 'Otros',
  otro: 'Otros',
};

/**
 * Non-restriction placeholder values to ignore.
 */
export const NON_RESTRICTION_KEYWORDS_SET = new Set<string>([
  'ninguna',
  'ninguno',
  'none',
  'sin restriccion',
  'sin restricciones',
  'no',
  'n/a',
  'na',
  '-',
  '--',
  'no aplica',
  'null',
  'undefined',
  'otros',
  'otro',
  'desconocida',
  'desconocido',
  'unknown',
  'empty',
  'sin especificar',
  'no especificado',
  'no especifica',
  's/n',
  'sn',
]);

/**
 * Structured passenger dietary restriction detail.
 */
export interface PassengerDietaryRestrictionDetail {
  passengerName: string;
  passengerType: 'PAX' | 'TC';
  restrictions: string[];
  rawSummary: string;
  hasAllergiesOrCritical: boolean;
}

/**
 * Represents a passenger record belonging to a file.
 */
export interface PicnicPassengerRecord {
  passengerId: string;
  fullName: string;
  age: number;
  type: 'pax' | 'tc';
  estado: boolean;
  restriccionesAlimentarias: string[];
  alergias?: string;
  isInfant?: boolean;
  isChild?: boolean;
}

/**
 * Represents the grouped Picnic details (Rows 9 to 17 + STAFF in specification).
 * Includes Pax count, Tour Conductors, Driver services, Guide services, Assistant Guide services,
 * Staff services, Infant passengers, Child passengers, and Dietary restrictions.
 */
export interface PicnicDetailsGroup {
  activePaxCount: number;
  activeTcCount: number;
  driverCount: number;
  guiaCount: number;
  asistGuiaCount: number;
  fieldStaffCount: number;
  staffCount: number;
  totalMesaCount: number;
  infantCount: number;
  childCount: number;
  dietaryRestrictionsList: string[];
}

/**
 * Summary metrics for an operational service category (Empresas de Transporte, Asistentes de Picnic, Bañero).
 */
export interface PicnicOperationalServiceSummary {
  totalCount: number;
  generatedOrdersCount: number;
  pendingOrdersCount: number;
  /** OS en PRELIMINAR (subset de generatedOrdersCount). */
  preliminaryOrdersCount?: number;
}

/**
 * Represents a row item in the Cuadro Picnic table.
 * Maps to the specification rows + STAFF column.
 */
export interface CuadroPicnicItem {
  id: string;
  idActivity?: string;
  idItinerary?: string;
  fechaOperacion: string;
  fileNumber: string;
  nameClient: string;
  nombreItinerario: string;
  isSSFFPadre?: boolean | null;
  // 4. reservationExecutives: List of requestedByEmail from purchase notes
  reservationExecutives?: string[];
  guiaPrincipal: string;
  foodType: string;
  activityName: string;
  servicioNombre?: string;
  picnicGroupDetails: PicnicDetailsGroup;
  empresaTransporte: PicnicOperationalServiceSummary;
  asistentePicnicServices: PicnicOperationalServiceSummary;
  baneroService: PicnicOperationalServiceSummary;
  operadorPicnicService?: PicnicOperationalServiceSummary;
  // 23. Revisado Pedido (Check del coordinador de AyB / Core)
  isPicnicOrderReviewed?: boolean;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  revisadoBiblia: boolean | null;
  observacionProgramadora: string;
  hasConsecutiveSnackAlert: boolean;
  consecutiveSnackDates: string[];
  totalPersonas: number;
}

/**
 * Represents KPI metrics displayed in the top header cards.
 */
export interface CuadroPicnicSummaryMetrics {
  snackCount: number;
  picnicCount: number;
  boxLunchCount: number;
  donativoCount: number;
  trekInkaTrailCount: number;
  trekAusangateCount: number;
  trekSalkantayCount: number;
  hiddenValleyCount: number;
}

/**
 * Represents criteria applied for filtering the table rows.
 */
export interface CuadroPicnicFilterCriteria {
  searchKeyword: string;
  startDate: Date | null;
  endDate: Date | null;
  selectedServiceType: string;
  selectedYear: string;
  exclusiveStartKeyId?: string;
}

/**
 * Common row structure from Cuadro Picnic GraphQL query.
 */
export interface CuadroPicnicRowGraphQLDto {
  id: string;
  idActivity: string;
  idItinerary: string;
  fechaOperacion: string;
  fileNumber: string;
  nameClient: string;
  nombreItinerario: string;
  isSSFFPadre?: boolean | null;
  reservationExecutives: string[];
  guiaPrincipal?: string | null;
  foodType: string;
  activityName: string;
  servicio?: string;
  picnicDetails: {
    paxCount: number;
    tcCount: number;
    driverCount: number;
    guiaCount: number;
    asistGuiaCount: number;
    fieldStaffCount?: number;
    staffCount: number;
    totalCount?: number;
    infantCount: number;
    childCount: number;
    dietaryRestrictionsSummary: string[];
  };
  empresaTransporte: PicnicOperationalServiceSummary;
  asistentesPicnic: PicnicOperationalServiceSummary;
  banero: PicnicOperationalServiceSummary;
  operadorPicnic?: PicnicOperationalServiceSummary;
  isPicnicOrderReviewed: boolean;
  revisadoBiblia: boolean | null;
  observacionProgramadora?: string | null;
}

/**
 * GraphQL API response structure for Cuadro Picnic queries.
 * Supports getCuadroPicnicPage from api-explorandes-core.
 */
export interface CuadroPicnicGraphQLResponse {
  getCuadroPicnicPage?: {
    items: CuadroPicnicRowGraphQLDto[];
    nextCursor?: string | null;
    totalCount: number;
  };
  getCuadroPicnic?: {
    items: CuadroPicnicRowGraphQLDto[];
    lastEvaluatedKeyId?: string;
    totalCount: number;
    summaryMetrics?: {
      totalSnack: number;
      totalPicnic: number;
      totalBoxLunch: number;
      totalTreks: number;
    };
    consecutiveSnackWarnings?: ConsecutiveSnackWarning[];
  };
}

/**
 * 3-day consecutive snack operational alert item.
 */
export interface ConsecutiveSnackWarning {
  fileNumber: string;
  clientName: string;
  nombreItinerario?: string;
  reservationExecutives?: string[];
  consecutiveDates: string[];
  warningMessage?: string;
}

/**
 * Represents an aggregated food & beverage service item in the details modal.
 */
export interface GroupedPicnicServiceItem {
  destinatarioServicio: string;
  nombreServicio: string;
  nombreProveedor?: string;
  count: number;
}

/**
 * Detailed activity breakdown payload returned by getPicnicActivityDetails.
 */
export interface PicnicActivityDetailsData {
  idActivity: string;
  aybServices: GroupedPicnicServiceItem[];
  lastEvaluatedKeyId?: string;
}

/**
 * GraphQL response wrapper for getPicnicActivityDetails query.
 */
export interface PicnicActivityDetailsGraphQLResponse {
  getPicnicActivityDetails: PicnicActivityDetailsData;
}

/**
 * Domain entity representing an operational picnic order review record in Core API.
 */
export interface PicnicOrderReviewItem {
  idActivity: string;
  tipoAlimento: string;
  idItinerary: string;
  isReviewed: boolean;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
}

/**
 * Input payload for toggling picnic order review status in Core API.
 */
export interface SetPicnicOrderReviewedInput {
  idActivity: string;
  tipoAlimento: string;
  isReviewed: boolean;
  reviewedBy?: string | null;
}

/**
 * GraphQL query response wrapper for getPicnicOrderReviewsByItinerary.
 */
export interface PicnicOrderReviewsGraphQLResponse {
  getPicnicOrderReviewsByItinerary: PicnicOrderReviewItem[];
}

/**
 * GraphQL mutation response wrapper for setPicnicOrderReviewed.
 */
export interface SetPicnicOrderReviewedGraphQLResponse {
  setPicnicOrderReviewed: PicnicOrderReviewItem;
}
